import { ToolUseBlock } from '../domTools'
import { HistoryEntry } from './history'

export const THINKING_STATUS = 'Thinking…'

export type SubscriptionVars = {
  message: string
  history: HistoryEntry[]
  pageContext: string
  skill?: string | null
}

export type StreamingState = {
  inProgressText: string
  currentStatus: string | null
  subscriptionVars: SubscriptionVars | null
  pendingCommit: string | null
  pendingDomTools: ToolUseBlock[] | null
  // Accumulates tool_use/tool_result pairs across a multi-step DOM workflow so
  // that each subscription reopen carries the full tool history, not just the
  // last pair. Cleared on SEND (new user message) and accumulated on RESUME
  // (DOM tool continuation).
  pendingToolTurns: HistoryEntry[]
}

export type StreamingAction =
  | { type: 'SEND'; vars: SubscriptionVars }
  | { type: 'RESUME'; vars: SubscriptionVars; newTurns: HistoryEntry[] }
  | { type: 'TOKEN'; content: string }
  | { type: 'TOOL_STATUS'; content: string | null }
  | { type: 'DONE' }
  | { type: 'ERROR'; content: string }
  | { type: 'COMMITTED' }
  | { type: 'DOM_TOOL_CALL'; content: ToolUseBlock[] }
  | { type: 'TOOL_TURN'; pair: [HistoryEntry, HistoryEntry] }
  | { type: 'STOP' }
  | { type: 'CLEAR' }

export const INITIAL_STATE: StreamingState = {
  inProgressText: '',
  currentStatus: null,
  subscriptionVars: null,
  pendingCommit: null,
  pendingDomTools: null,
  pendingToolTurns: []
}

export function streamingReducer(state: StreamingState, action: StreamingAction): StreamingState {
  switch (action.type) {
    case 'SEND':
      return {
        inProgressText: '',
        currentStatus: THINKING_STATUS,
        subscriptionVars: action.vars,
        pendingCommit: null,
        pendingDomTools: null,
        pendingToolTurns: []
      }
    case 'RESUME':
      return {
        inProgressText: '',
        currentStatus: THINKING_STATUS,
        subscriptionVars: action.vars,
        pendingCommit: null,
        pendingDomTools: null,
        pendingToolTurns: [...state.pendingToolTurns, ...action.newTurns]
      }
    case 'TOKEN':
      if (state.subscriptionVars === null) return state
      return {
        ...state,
        inProgressText: state.inProgressText + action.content,
        currentStatus: null
      }
    case 'TOOL_STATUS':
      if (state.subscriptionVars === null) return state
      return { ...state, currentStatus: action.content }
    case 'DONE':
      if (state.subscriptionVars === null) return state
      return {
        ...state,
        inProgressText: '',
        currentStatus: null,
        subscriptionVars: null,
        pendingCommit: state.inProgressText !== '' ? state.inProgressText : null
      }
    case 'ERROR': {
      if (state.subscriptionVars === null) return state
      // The server flushes whatever it had already streamed as a final TOKEN
      // before reporting the failure, so a nearly-complete answer is not lost
      // to a transient blip. Commit that text above the error notice; sending
      // only the error discards work the advisor would otherwise have to
      // request again from the start.
      const partial = state.inProgressText.trimEnd()
      return {
        ...state,
        inProgressText: '',
        currentStatus: null,
        subscriptionVars: null,
        pendingCommit: partial === '' ? action.content : `${partial}\n\n${action.content}`,
        pendingDomTools: null
      }
    }
    case 'COMMITTED':
      return { ...state, pendingCommit: null }
    case 'DOM_TOOL_CALL':
      if (state.subscriptionVars === null) return state
      return {
        ...state,
        inProgressText: '',
        currentStatus: null,
        subscriptionVars: null,
        pendingDomTools: action.content
      }
    case 'TOOL_TURN':
      if (state.subscriptionVars === null) return state
      return {
        ...state,
        pendingToolTurns: [...state.pendingToolTurns, ...action.pair]
      }
    case 'STOP':
      // Preserves any in-flight assistant text as a pendingCommit so a
      // half-streamed paragraph survives in chat history. Nulling
      // subscriptionVars here is what makes every other event-driven
      // action (TOKEN/TOOL_STATUS/DONE/ERROR/DOM_TOOL_CALL/TOOL_TURN)
      // bail via the guard above when buffered events arrive after stop.
      return {
        ...state,
        subscriptionVars: null,
        pendingCommit: state.inProgressText !== '' ? state.inProgressText : null,
        inProgressText: '',
        currentStatus: null,
        pendingDomTools: null,
        pendingToolTurns: []
      }
    case 'CLEAR':
      // Unlike STOP, a kept pendingCommit would land in the emptied panel as a
      // bubble with no question above it.
      return INITIAL_STATE
    default:
      return state
  }
}
